import type { ComponentProps, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useOpenAppModal } from '~/lib/appModal';
import { GET_THE_APP_PATH, isMobileClient, storeUrlForClient } from '~/lib/appStores';
import { cn } from '~/lib/cn';
import { trackAppDownloadLead } from '~/lib/track';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline';
type Size = 'md' | 'lg';

const variantClasses: Record<Variant, string> = {
  primary: 'bg-red-500 text-white hover:bg-red-700',
  secondary: 'bg-grey-50 text-ink hover:bg-grey-100',
  ghost: 'bg-transparent text-grey-900 hover:bg-grey-50',
  // White fill + light border — reads as outlined on light page backgrounds.
  outline: 'border border-grey-100 bg-white text-ink hover:bg-grey-50',
};

// Padding from Figma button component: 12 top / 14 right / 13 bottom / 14 left.
const sizeClasses: Record<Size, string> = {
  md: 'pt-[12px] pr-[14px] pb-[13px] pl-[14px] text-[16px] tracking-[0.192px]',
  lg: 'pt-[15px] pr-[20px] pb-[16px] pl-[20px] text-[17px]',
};

interface ButtonBaseProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
}

function buttonClasses({
  variant = 'primary',
  size = 'md',
  className,
}: Omit<ButtonBaseProps, 'children'>) {
  return cn(
    'inline-flex min-w-16 cursor-pointer items-center justify-center rounded-pill font-medium leading-[1.4] transition-colors',
    variantClasses[variant],
    sizeClasses[size],
    className,
  );
}

export function Button({
  variant,
  size,
  className,
  children,
  type = 'button',
  ...props
}: ButtonBaseProps & ComponentProps<'button'>) {
  return (
    <button className={buttonClasses({ variant, size, className })} type={type} {...props}>
      {/* Label wrapped so text-box-trim applies (a flex container's raw text is not trimmable). */}
      <span>{children}</span>
    </button>
  );
}

export function ButtonLink({
  variant,
  size,
  className,
  children,
  href,
  external,
}: ButtonBaseProps & { href: string; external?: boolean }) {
  const openAppModal = useOpenAppModal();
  const classes = buttonClasses({ variant, size, className });

  // App download CTAs: mobile → store directly; desktop → QR modal.
  // Render a stable <a> for hydration; behavior branches in onClick.
  if (href === GET_THE_APP_PATH) {
    return (
      <a
        href={GET_THE_APP_PATH}
        className={classes}
        onClick={(e) => {
          const isMobile = isMobileClient();
          trackAppDownloadLead(isMobile ? 'mobile' : 'qr');
          if (isMobile) {
            e.preventDefault();
            window.location.href = storeUrlForClient();
            return;
          }
          if (openAppModal) {
            e.preventDefault();
            openAppModal();
          }
        }}>
        <span>{children}</span>
      </a>
    );
  }

  // External URLs can't use the router Link (same pattern as partnerships / nav).
  const isExternal = external ?? /^https?:\/\//.test(href);
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={classes}>
        <span>{children}</span>
      </a>
    );
  }

  return (
    <Link to={href} className={classes}>
      <span>{children}</span>
    </Link>
  );
}
